import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import client from "@/lib/apiClient";

/**
 * Shared layout for all /legal/* policy pages.
 *
 * Each page passes its bundled default (`title`, `lastUpdated`, `sections`) plus its
 * `slug`. On mount we call GET /api/legal-pages/{slug}: if an admin has saved an
 * override it replaces the bundled content; otherwise the bundled default is shown
 * (so nothing changes until an admin explicitly clicks Save in the Admin panel).
 */
export function LegalLayout({ slug, title, lastUpdated, sections }) {
  const [override, setOverride] = useState(null);

  useEffect(() => {
    let alive = true;
    if (slug) {
      client.get(`/legal-pages/${slug}`).then(({ data }) => {
        if (!alive) return;
        if (data && data.override) setOverride(data);
      }).catch(() => { /* fall back to bundled default */ });
    }
    return () => { alive = false; };
  }, [slug]);

  const activeTitle = override?.title || title;
  const activeLastUpdated = override?.last_updated || lastUpdated;
  const activeSections = override?.sections || sections;

  useEffect(() => {
    const prev = document.title;
    document.title = `${activeTitle} · AdGen Store`;
    return () => { document.title = prev; };
  }, [activeTitle]);

  return (
    <div className="mx-auto max-w-3xl px-6 py-14 sm:px-8" data-testid={`legal-page-${activeTitle.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="legal-back-home">
        <ArrowLeft className="h-4 w-4" /> Back to home
      </Link>

      <h1 className="mt-6 font-display text-4xl font-extrabold tracking-tight sm:text-5xl">{activeTitle}</h1>
      <p className="mt-3 text-sm text-muted-foreground">Last updated: {activeLastUpdated}</p>

      <div className="mt-10 space-y-8 text-[15px] leading-relaxed text-muted-foreground">
        {activeSections.map((s, i) => (
          <section key={`${i}-${s.heading}`} data-testid={`legal-section-${i + 1}`}>
            <h2 className="mb-3 font-display text-xl font-bold text-foreground">
              <span className="mr-2 text-primary">{i + 1}.</span>{s.heading}
            </h2>
            {s.body.map((para, j) => (
              <p key={j} className="mb-3 last:mb-0 whitespace-pre-line">{para}</p>
            ))}
          </section>
        ))}
      </div>

      <p className="mt-12 border-t border-border pt-6 text-xs text-muted-foreground">
        Questions? Reach us via our <Link to="/contact" className="text-primary underline-offset-4 hover:underline">Contact page</Link>.
      </p>
    </div>
  );
}
